import { MoreHorizontalIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

import type { DataTableAction, DataTableRowActionsProps } from './data-table.interface'

const DataTableRowActionsBase = <TData,>({
  row,
  actionList,
  className,
}: DataTableRowActionsProps<TData>): ReactNode => {
  const defaultActionList = actionList.filter((action) => action.variant !== 'destructive')
  const destructiveActionList = actionList.filter((action) => action.variant === 'destructive')

  const renderAction = (action: DataTableAction<TData>): ReactNode => {
    const isDisabled = action.isDisabled?.(row) ?? false

    return (
      <DropdownMenuItem
        key={action.id}
        onClick={() => action.onClick(row)}
        disabled={isDisabled}
        variant={action.variant}
      >
        {action.icon && <span className="mr-2">{action.icon}</span>}
        {action.label}
      </DropdownMenuItem>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('data-table-row-actions size-8', className)}
        >
          <MoreHorizontalIcon className="size-4" />
          <span className="sr-only">Open actions menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        {defaultActionList.map(renderAction)}
        {destructiveActionList.length > 0 && defaultActionList.length > 0 && (
          <DropdownMenuSeparator />
        )}
        {destructiveActionList.map(renderAction)}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const DataTableRowActions = memo(DataTableRowActionsBase) as typeof DataTableRowActionsBase
